import { combineEpics, ofType } from "redux-observable";
import { v4 as uuidv4 } from "uuid";
import * as RxOp from "rxjs/operators";
import * as Rx from "rxjs";
import S from "sanctuary";
import moment from "moment-timezone";
import { transactionSearch, translateTransactionSearch } from "@/APIClient";
import { requestEpic } from "@/APIClient/observable.util";
import {
  changeCalendarDates,
  closeDatesModal,
} from "@/components/datePicker/DatePicker.state";
import {
  getUserTimezone,
  getCurrentUserClientBrand,
  getCurrentUserAgencyNames,
} from "@/components/currentUser/CurrentUser.state";
import {
  getTransactionSearchStartDate,
  getTransactionSearchEndDate,
} from "@/components/datePicker/DatePicker.selectors";
import { getDefaultSearchDates } from "@/components/datePicker/Search.dates.util";
import {
  createAlert,
  clearAllAlerts,
  ALERT_LEVELS,
} from "@/components/alerts/Alerts.state";
import {
  clearState,
  setTransactionSearchResults,
  getActiveSearchId,
  RESULTS_PER_PAGE,
} from "@/components/transactionSearchResults/TransactionSearchResults.state";
import { getAuthToken } from "@/util/session";

import {
  TRANSACTION_SEARCH_PENDING,
  SEARCH_REQUEST_FAILED,
  SEARCH_REQUEST_SUCCEEDED,
  REQUEST_SEARCH,
  PERFORM_SEARCH,
  REQUEST_PAGINATED_SEARCH,
  TRANSLATE_REQUEST,
  transactionSearchPending,
  getTransactionDateFilter,
  searchRequestSucceeded,
  searchRequestFailed,
  translateSucceeded,
  translateFailed,
} from "./TransactionSearchPage.state";
import { nextPendingTextFrame, closeModal } from "./TransactionSearchPage.state";

import { AGENCIES_FILTER, isMultiValueFilter } from "./FiltersForm";
import { getAmountPredicate, amountMatchesPredicate } from "./amountFilter";

// BE only exact-matches `amount`; predicates filter client-side over pages
// of this size
const AMOUNT_FILTER_FETCH_SIZE = 200;
// Ceiling so a broad predicate search can't page indefinitely.
const AMOUNT_FILTER_MAX_ROWS = 5000;

const updateSearchPendingTextEpic = (action$) =>
  action$.pipe(
    ofType(TRANSACTION_SEARCH_PENDING),
    RxOp.mergeMap(() =>
      Rx.timer(800, 800)
        .pipe(RxOp.mapTo(nextPendingTextFrame()))
        .pipe(
          RxOp.takeUntil(
            action$.pipe(ofType(SEARCH_REQUEST_FAILED, SEARCH_REQUEST_SUCCEEDED))
          )
        )
    )
  );

const searchCriteria = (state) => {
  const criteriaForm = state.transactionSearch.forms.criteriaForm;
  return Object.keys(criteriaForm).reduce((acc, key) => {
    let value = criteriaForm[key].rawValue;
    if (value === "") {
      return acc;
    } else {
      return { ...acc, [key]: value };
    }
  }, {});
};

const searchFilters = (state) => {
  const filtersForm = state.transactionSearch.forms.filtersForm;
  return Object.keys(filtersForm).reduce((acc, key) => {
    const value = filtersForm[key].rawValue;
    if (value === "") {
      if (key === AGENCIES_FILTER) {
        return { ...acc, [key]: getCurrentUserAgencyNames(state) };
      }
      return acc;
    }
    // Multi-value filters (per the multi-select RMD handoff) ship the rawValue as a comma-separated string; the /payments endpoint expects each value as a separate `param[]=` entry, which qs.stringify produces when we hand it an array. Single-value filters (paymentMode) keep the scalar.
    if (isMultiValueFilter(key)) {
      return {
        ...acc,
        [key]: value
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean),
      };
    }
    return { ...acc, [key]: value };
  }, {});
};

const searchDates = (state, overrideDates = null) => {
  const dateFilter = getTransactionDateFilter(state);
  const timezone = getUserTimezone(state).value;

  // When the epic resolves an override, send exactly those bounds (either may
  // be null to omit it); do not mirror one bound onto the other.
  if (overrideDates) {
    const { startDate, endDate } = overrideDates;
    if (!startDate && !endDate) return {};
    return {
      ...(startDate
        ? {
            startDate: moment(startDate).startOf("day").tz(timezone).toISOString(),
          }
        : {}),
      ...(endDate
        ? { endDate: moment(endDate).endOf("day").tz(timezone).toISOString() }
        : {}),
      dateFilter,
    };
  }

  let startDate = getTransactionSearchStartDate(state);
  let endDate = getTransactionSearchEndDate(state);
  startDate = startDate || endDate;
  endDate = endDate || startDate;
  return startDate && endDate
    ? {
        startDate: moment(startDate).startOf("day").tz(timezone).toISOString(),
        endDate: moment(endDate).endOf("day").tz(timezone).toISOString(),
        dateFilter,
      }
    : {};
};

const getAmountPredicateFromState = (state) =>
  getAmountPredicate(state.transactionSearch.forms.criteriaForm.amount?.rawValue);

/**
 * fetchAllForAmountPredicate
 *
 * Drop-in for `transactionSearch` in `requestEpic`. Pages until every
 * row is fetched or AMOUNT_FILTER_MAX_ROWS is reached, so the predicate is
 * applied over the whole set rather than one window. Returns a cold observable
 * rather than a promise: no page is requested until it is subscribed, and
 * unsubscribing (a newer search superseding this one) stops the paging instead
 * of letting it run on against an abandoned search.
 * @param {object} requestArgs - Request args; page/resultsPerPage overridden.
 * @returns {object} - An observable of `S.Right({ transactions, total,
 * truncated })`, or the first failing page's `S.Left(error)`.
 */
export const fetchAllForAmountPredicate = (requestArgs) => {
  const baseArgs = { ...requestArgs };
  delete baseArgs.page;
  delete baseArgs.resultsPerPage;
  const pageFrom = (page, rows) =>
    Rx.defer(() =>
      transactionSearch({
        ...baseArgs,
        page,
        resultsPerPage: AMOUNT_FILTER_FETCH_SIZE,
      })
    ).pipe(
      RxOp.mergeMap((result) => {
        if (S.isLeft(result)) return Rx.of(result);
        const { transactions, total } = result.value;
        const all = rows.concat(transactions);
        const serverTotal = S.fromMaybe(all.length)(total);
        // An empty page means there is no more to fetch, whatever the total says.
        const done =
          transactions.length === 0 ||
          all.length >= serverTotal ||
          all.length >= AMOUNT_FILTER_MAX_ROWS;
        return done
          ? Rx.of(
              S.Right({
                transactions: all,
                total: S.Just(serverTotal),
                truncated:
                  all.length >= AMOUNT_FILTER_MAX_ROWS && all.length < serverTotal,
              })
            )
          : pageFrom(page + 1, all);
      })
    );
  return pageFrom(1, []);
};

/**
 * transactionAmount
 *
 * Pull the numeric dollar amount off a transaction in the form returned by
 * PAYMENTS_SEARCH. `subtotal` is a `Maybe String` of the formatted amount, e.g.
 * `S.Just("1,234.56")`. Returns null when the value is missing or unparsable so
 * those rows are dropped from a filtered result set rather than silently
 * passing the predicate.
 * @param {object} transaction - The transaction record from PAYMENTS_SEARCH.
 * @returns {number|null} - The numeric dollar amount, or null when missing or
 * unparsable.
 */
export const transactionAmount = (transaction) => {
  const subtotal = transaction?.subtotal;
  if (!subtotal || S.isNothing(subtotal)) return null;
  const raw = String(subtotal.value).replace(/,/g, "");
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : null;
};

const userInfo = (state) => ({
  token: S.fromMaybe("")(getAuthToken(state)),
  client: getCurrentUserClientBrand(state),
});

const searchParams = (state, page, overrideDates = null) => ({
  ...userInfo(state),
  page,
  resultsPerPage: RESULTS_PER_PAGE,
  ...searchDates(state, overrideDates),
  ...searchFilters(state),
  ...searchCriteria(state),
});

// Given the picker's raw start/end (moments or null), decide the date range to
// use when a search is started:
// - an explicit start is always honored (return null to use state as-is);
// - no dates -> a trailing year (one year ago through today);
// - end only, within the last year -> fill start to one year ago;
// - end only, more than a year ago -> no start (search from the beginning of
//   time up to the end), since a one-year-ago start would invert the range.
export const resolveSearchDateDefaults = (rawStartDate, rawEndDate) => {
  if (rawStartDate) return null;
  const { startDate: oneYearAgo, endDate: today } = getDefaultSearchDates();
  if (!rawEndDate) return { startDate: oneYearAgo, endDate: today };
  return moment(rawEndDate).isSameOrAfter(oneYearAgo, "day")
    ? { startDate: oneYearAgo, endDate: rawEndDate }
    : { startDate: null, endDate: rawEndDate };
};

const performTransactionSearchEpic = (action$, state$) =>
  action$.pipe(
    ofType(REQUEST_SEARCH, REQUEST_PAGINATED_SEARCH),
    RxOp.mergeMap(({ payload = {} }) => {
      // The identity this search settles under
      const searchId = payload.searchId ?? uuidv4();
      // Captured so the response is filtered by the predicate it was requested
      // with, not whatever the pill holds when it settles.
      const amountPredicate = getAmountPredicateFromState(state$.value);
      // The results slice names the search the page is waiting on. Anything
      // else settling belongs to criteria the user has moved past, so it must
      // not clear the pending state or raise notices
      const isSuperseded = () => {
        const activeSearchId = getActiveSearchId(state$.value);
        return Boolean(activeSearchId) && activeSearchId !== searchId;
      };
      // Resolve default dates when a search is started.
      const rawStartDate = getTransactionSearchStartDate(state$.value);
      const rawEndDate = getTransactionSearchEndDate(state$.value);
      const overrideDates = resolveSearchDateDefaults(rawStartDate, rawEndDate);
      const filledDefaultStart = Boolean(overrideDates && overrideDates.startDate);
      const defaultDateActions = filledDefaultStart
        ? [changeCalendarDates(overrideDates, false, true)]
        : [];
      const optimizedRangeAlert = filledDefaultStart
        ? [
            createAlert({
              type: ALERT_LEVELS.INFO,
              title: "Date Range Optimized",
              text: "Your search was too broad. We applied a 1-year date range to prevent the system from timing out. If you would like a different date range please adjust the dates.",
              ttl: S.Nothing,
            }),
          ]
        : [];
      return Rx.concat([
        Rx.of(
          // Every (re)submitted search starts with a clean alert stack;
          // notices raised for the previous search no longer apply to this
          // one. Emitted ahead of `optimizedRangeAlert` so a notice for THIS
          // search survives the sweep.
          clearAllAlerts(),
          ...defaultDateActions,
          ...optimizedRangeAlert,
          // Names the search the results slice is waiting on, so a response
          // that lost the race is dropped there
          clearState({ searchId }),
          closeModal(),
          closeDatesModal(),
          transactionSearchPending()
        ),
        requestEpic({
          requestMethod: amountPredicate
            ? fetchAllForAmountPredicate
            : transactionSearch,
          requestArgs: searchParams(state$.value, payload.page, overrideDates),
          onSuccess: (result) => {
            if (isSuperseded()) return Rx.EMPTY;
            const rawTransactions = result.value.transactions;
            const serverTotal = S.fromMaybe(rawTransactions.length)(
              result.value.total
            );
            // drop non-matching rows bc legacy `/payments` API can't filter by amount range/operator displayed count should reflect what user actually sees
            const transactions = amountPredicate
              ? rawTransactions.filter((txn) =>
                  amountMatchesPredicate(transactionAmount(txn), amountPredicate)
                )
              : rawTransactions;
            const resultsCount = amountPredicate ? transactions.length : serverTotal;
            // Matches past AMOUNT_FILTER_MAX_ROWS are invisible; say so.
            const windowTruncated =
              Boolean(amountPredicate) && Boolean(result.value.truncated);
            return Rx.of(
              searchRequestSucceeded({ searchId }),
              setTransactionSearchResults({
                transactions,
                resultsCount,
                page: amountPredicate ? 1 : payload.page || 1,
                searchId,
                clientPaginated: Boolean(amountPredicate),
              }),
              ...(windowTruncated
                ? [
                    createAlert({
                      type: ALERT_LEVELS.INFO,
                      title: `Showing the First ${AMOUNT_FILTER_MAX_ROWS} Transactions`,
                      text: `Amount ranges are matched in your browser, against the first ${AMOUNT_FILTER_MAX_ROWS} transactions your other criteria returned. Matches beyond those are not shown. Narrow your date range or filters to be sure you are seeing every match.`,
                      ttl: S.Nothing,
                    }),
                  ]
                : [])
            );
          },
          onFailure: (error) =>
            isSuperseded()
              ? Rx.EMPTY
              : Rx.of(searchRequestFailed({ error: error.value, searchId })),
        }).pipe(
          // A newer search supersedes this one: stop paging and drop whatever
          // this search would have settled with (WCHK-3037).
          RxOp.takeUntil(
            action$.pipe(
              ofType(PERFORM_SEARCH, REQUEST_SEARCH, REQUEST_PAGINATED_SEARCH)
            )
          )
        ),
      ]);
    }),
    RxOp.concatAll()
  );

/**
 * toTranslateError
 *
 * Map a raw client error into the `{code, message}` envelope so the UI can
 * branch on `code` and show `message`. `error.body` is populated by
 * `rawResponse` from the JSON response on non-2xx; otherwise fall back
 * to inferring a code from the HTTP status.
 * @param {object} rawError - The raw client error.
 * @returns {object} - The { code, message } error envelope.
 */
export const toTranslateError = (rawError) => {
  const envelope = rawError?.body?.error;
  if (envelope?.code && envelope?.message) {
    return { code: envelope.code, message: envelope.message };
  }
  const status = rawError?.status;
  if (status === 400) {
    return {
      code: "invalid_input",
      message: "Your search couldn't be understood. Try rephrasing it.",
    };
  }
  if (status === 504) {
    return {
      code: "timeout",
      message: "The translation timed out. Please try again.",
    };
  }
  if (status === 502) {
    return {
      code: "translation_failed",
      message: "Couldn't translate the query. Please try again.",
    };
  }
  return {
    code: "translation_failed",
    message: rawError?.message || "Couldn't translate the query. Please try again.",
  };
};

const translateRequestEpic = (action$, state$) =>
  action$.pipe(
    ofType(TRANSLATE_REQUEST),
    RxOp.mergeMap(({ payload }) => {
      const state = state$.value;
      const timezoneMaybe = getUserTimezone(state);
      const timezone = S.isJust(timezoneMaybe) ? timezoneMaybe.value : undefined;
      // A natural-language submit is the other way a search is (re)submitted,
      // so it sweeps the alert stack the same way the search epic does.
      return Rx.concat(
        Rx.of(clearAllAlerts()),
        requestEpic({
          requestMethod: translateTransactionSearch,
          requestArgs: {
            token: S.fromMaybe("")(getAuthToken(state)),
            query: payload.query,
            agencyContext: getCurrentUserAgencyNames(state) || [],
            timezone,
          },
          onSuccess: (result) => Rx.of(translateSucceeded({ result: result.value })),
          onFailure: (error) =>
            Rx.of(translateFailed({ error: toTranslateError(error.value) })),
        })
      );
    })
  );

export default combineEpics(
  updateSearchPendingTextEpic,
  performTransactionSearchEpic,
  translateRequestEpic
);
